import { STAGE } from '../stage.js';
import { GROUND_FIT } from './measure.js';
import { metres } from './util.js';
export function readHeights(bytes, spec) {
    const count = spec.columns * spec.rows;
    if (bytes.byteLength !== count * 4) {
        throw new Error(`hoogte.bin is ${bytes.byteLength} bytes; ${spec.columns} × ${spec.rows} hoogtes zijn ${count * 4}`);
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const data = new Float32Array(count);
    for (let i = 0; i < count; i++)
        data[i] = view.getFloat32(i * 4, true);
    return { spec, data };
}
/** The bytes of a height map sampled from `height` at every node (the kit, and tests). */
export function writeHeights(spec, height) {
    const bytes = new Uint8Array(spec.columns * spec.rows * 4);
    const view = new DataView(bytes.buffer);
    for (let r = 0; r < spec.rows; r++) {
        const z = spec.z[0] + ((spec.z[1] - spec.z[0]) * r) / (spec.rows - 1);
        for (let c = 0; c < spec.columns; c++) {
            const x = spec.x[0] + ((spec.x[1] - spec.x[0]) * c) / (spec.columns - 1);
            view.setFloat32((r * spec.columns + c) * 4, height(x, z), true);
        }
    }
    return bytes;
}
/** The ground's height at (x, z): bilinear between nodes, the edge's outside the grid. */
export function heightAt(grid, x, z) {
    const { spec, data } = grid;
    const fx = ((Math.min(Math.max(x, spec.x[0]), spec.x[1]) - spec.x[0]) / (spec.x[1] - spec.x[0])) *
        (spec.columns - 1);
    const fz = ((Math.min(Math.max(z, spec.z[0]), spec.z[1]) - spec.z[0]) / (spec.z[1] - spec.z[0])) *
        (spec.rows - 1);
    const c0 = Math.min(Math.floor(fx), spec.columns - 2);
    const r0 = Math.min(Math.floor(fz), spec.rows - 2);
    const tx = fx - c0;
    const tz = fz - r0;
    const at = (r, c) => data[r * spec.columns + c];
    const top = at(r0, c0) * (1 - tx) + at(r0, c0 + 1) * tx;
    const bottom = at(r0 + 1, c0) * (1 - tx) + at(r0 + 1, c0 + 1) * tx;
    return top * (1 - tz) + bottom * tz;
}
const dutch = (n) => n.toFixed(2).replace('.', ',');
/** The widest the nodes may stand apart (metres): the flat standing area is judged at the nodes, so they are dense. */
export const NODE_SPACING_MAX = 1;
/**
 * ADR-061's rule as numbers: every height a number, the clear cylinder covered (4.5 m), the nodes at
 * most 1 m apart, every node of every cell that touches the standing area flat at 0 (±2 cm), and none
 * that touches the clear cylinder above 5 cm (it may fall away). A bilinear cell never leaves its four
 * nodes' range, so judging those nodes judges the surface.
 */
export function heightProblems(grid) {
    const { spec, data } = grid;
    const at = '/terrain/heights';
    if (data.some((h) => !Number.isFinite(h))) {
        return [{ path: at, message: 'bevat een hoogte die geen getal is' }];
    }
    if (data.some((h) => Math.abs(h) > 1000)) {
        return [{ path: at, message: 'bevat een hoogte die te hoog of te laag is (hooguit 1000 m)' }];
    }
    const a = STAGE.clear;
    if (spec.x[0] > -a || spec.x[1] < a || spec.z[0] > -a || spec.z[1] < a) {
        return [
            {
                path: at,
                message: `moet de vrije ruimte rond het podium (${metres(a)} m rond het midden) bedekken`,
            },
        ];
    }
    const dx = (spec.x[1] - spec.x[0]) / (spec.columns - 1);
    const dz = (spec.z[1] - spec.z[0]) / (spec.rows - 1);
    if (dx > NODE_SPACING_MAX + 1e-9 || dz > NODE_SPACING_MAX + 1e-9) {
        return [
            {
                path: at,
                message: `de knopen liggen ${dutch(dx)} × ${dutch(dz)} m uit elkaar; hooguit ${NODE_SPACING_MAX} m, zodat de grond onder het podium echt vlak is`,
            },
        ];
    }
    // Every node of a cell that touches the disc: a bilinear cell never leaves its nodes' range.
    const slack = Math.hypot(dx, dz);
    let flat = 0;
    let highest = -Infinity;
    for (let r = 0; r < spec.rows; r++) {
        const z = spec.z[0] + dz * r;
        if (Math.abs(z) > a + slack)
            continue;
        for (let c = 0; c < spec.columns; c++) {
            const x = spec.x[0] + dx * c;
            const d = Math.hypot(x, z);
            const h = data[r * spec.columns + c];
            if (d <= STAGE.floor + slack)
                flat = Math.max(flat, Math.abs(h));
            if (d <= a + slack)
                highest = Math.max(highest, h);
        }
    }
    if (flat > STAGE.below + 1e-6) {
        return [
            {
                path: at,
                message: `de grond op het podium (${metres(STAGE.floor)} m) moet vlak zijn op 0 (±2 cm); hij komt tot ${dutch(flat)} m`,
            },
        ];
    }
    if (highest > STAGE.apronHeight + 1e-6) {
        return [
            {
                path: at,
                message: `de grond in de vrije ruimte rond het podium (${metres(a)} m) komt hoger dan 5 cm: tot ${dutch(highest)} m`,
            },
        ];
    }
    return [];
}
/** A place as a maker reads it: [12, -8,5]. */
const place = (at) => `[${metres(at[0])}, ${metres(at[1])}]`;
/** How far the ground lies from the map at a place, in words: "1,20 m lager". */
const apart = (by) => `${dutch(Math.abs(by))} m ${by < 0 ? 'lager' : 'hoger'}`;
/**
 * The visible ground (`measureGroundFit`) against the height map (16 Oct 2026): refused where they lie
 * more than 5 cm apart (more on a slope, `GROUND_FIT.slope`) over at least 4 m² together, naming the worst
 * places. Models stand on the map, so a model there floats (the ground lower) or sinks into it (the
 * ground higher). The message is the same in the kit and on the platform.
 */
export function groundFitProblems(fit) {
    // Sunk land (20 Oct 2026): no sample found ground, and a grond… mesh lies far below the floor.
    if (fit.sunk && fit.samples > 0 && fit.bare === fit.samples) {
        return [
            {
                path: '/terrain/heights',
                message: `Nergens binnen ${metres(GROUND_FIT.radius)} m ligt grond bij de hoogtekaart: een grond…-mesh ligt ` +
                    `meer dan ${metres(GROUND_FIT.depth)} m onder het podium, waar modellen niet op staan. Leg de grond ` +
                    `op de hoogtekaart, of laat de kit hoogte.bin uit de grond meten: ` +
                    `kit.heights(spec, hoogtefunctie, { uitGrond: true }), of kit.heights(spec) als alle grond grond… heet`,
            },
        ];
    }
    const area = (fit.patch ?? fit.off) * GROUND_FIT.step * GROUND_FIT.step;
    if (area < GROUND_FIT.area - 1e-9 || fit.worst.length === 0)
        return [];
    const [first, ...rest] = fit.worst;
    const others = rest.map((w) => `op ${place(w.at)} ${apart(w.by)}`);
    return [
        {
            path: '/terrain/heights',
            message: `Op ${place(first.at)} ligt de grond ${apart(first.by)} dan de hoogtekaart` +
                (others.length > 0 ? ` (en ${others.join(', ')})` : '') +
                `: over ${metres(Math.round(area))} m² aaneen wijkt de zichtbare grond meer dan ` +
                `${metres(GROUND_FIT.tolerance * 100)} cm af van hoogte.bin, waar modellen op staan ` +
                `(lager: een model zweeft; hoger: het zakt erin). Laat de kit hoogte.bin uit de grond meten: ` +
                `kit.heights(spec, hoogtefunctie, { uitGrond: true }), of kit.heights(spec) als alle grond grond… heet`,
        },
    ];
}
//# sourceMappingURL=heights.js.map